/* =========================================================================
   FF UI JS — gallery, hotspots, tabs, accordion group, reveal-on-scroll.
   Idempotent custom elements; listeners cleaned up on disconnect, so
   Theme Editor section reloads never stack duplicate handlers.
   ========================================================================= */
(function () {
  'use strict';

  function defineOnce(name, ctor) {
    if (!customElements.get(name)) customElements.define(name, ctor);
  }

  function makeElement(onConnect) {
    var proto = Object.create(HTMLElement.prototype);
    proto.connectedCallback = function () {
      this._abort = new AbortController();
      onConnect(this, this._abort.signal);
    };
    proto.disconnectedCallback = function () {
      if (this._abort) this._abort.abort();
      if (this._observer) { this._observer.disconnect(); this._observer = null; }
    };
    function Ctor() { return Reflect.construct(HTMLElement, [], Ctor); }
    Ctor.prototype = proto;
    return Ctor;
  }

  /* -----------------------------------------------------------------------
     <ff-reveal-scope> — fade-up on first view (CSS handles reduced motion)
  ----------------------------------------------------------------------- */
  defineOnce('ff-reveal-scope', makeElement(function (el) {
    var targets = el.querySelectorAll('.ff-reveal');
    if (!targets.length || !('IntersectionObserver' in window)) {
      targets.forEach(function (t) { t.classList.add('ff-in'); });
      return;
    }
    el._observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('ff-in');
          el._observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '-8% 0px -12%' });
    targets.forEach(function (t) { el._observer.observe(t); });
  }));

  /* -----------------------------------------------------------------------
     <ff-gallery> — product media gallery with thumbnails
  ----------------------------------------------------------------------- */
  defineOnce('ff-gallery', makeElement(function (el, signal) {
    var slides = el.querySelectorAll('[data-ff-slide]');
    var thumbs = el.querySelectorAll('[data-ff-thumb]');
    var productId = Number(el.getAttribute('data-product-id'));

    function select(index) {
      slides.forEach(function (slide, i) { slide.hidden = i !== index; });
      thumbs.forEach(function (thumb, i) {
        thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () { select(i); }, { signal: signal });
    });

    document.addEventListener('ff:variant-change', function (event) {
      var d = event.detail || {};
      if (d.productId !== productId || d.mediaPosition == null) return;
      var index = d.mediaPosition - 1;
      if (index >= 0 && index < slides.length) select(index);
    }, { signal: signal });

    if (slides.length) select(0);
  }));

  /* -----------------------------------------------------------------------
     <ff-hotspots> — image hotspots + expanding list, kept in sync
  ----------------------------------------------------------------------- */
  defineOnce('ff-hotspots', makeElement(function (el, signal) {
    var dots = el.querySelectorAll('[data-ff-dot]');
    var items = el.querySelectorAll('[data-ff-hotspot-item]');

    function select(key) {
      dots.forEach(function (dot) {
        dot.setAttribute('aria-expanded', dot.dataset.ffDot === key ? 'true' : 'false');
      });
      items.forEach(function (item) {
        var active = item.dataset.ffHotspotItem === key;
        item.setAttribute('aria-expanded', active ? 'true' : 'false');
        var body = item.querySelector('.ff-hotspot-item__body');
        if (body) body.hidden = !active;
      });
    }

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () { select(dot.dataset.ffDot); }, { signal: signal });
    });
    items.forEach(function (item) {
      item.addEventListener('click', function () { select(item.dataset.ffHotspotItem); }, { signal: signal });
    });

    /* Theme Editor: reveal the block a merchant selects */
    el.addEventListener('shopify:block:select', function (event) {
      var block = event.target && event.target.closest('[data-ff-hotspot-item]');
      if (block) select(block.dataset.ffHotspotItem);
    }, { signal: signal });

    if (items.length) select(items[0].dataset.ffHotspotItem);
  }));

  /* -----------------------------------------------------------------------
     <ff-tabs> — app feature tabs + phone screen swap
  ----------------------------------------------------------------------- */
  defineOnce('ff-tabs', makeElement(function (el, signal) {
    var tabs = Array.prototype.slice.call(el.querySelectorAll('[data-ff-tab]'));
    var screens = el.querySelectorAll('[data-ff-screen]');
    var screenLabel = el.querySelector('[data-ff-screen-label]');

    function select(index, focus) {
      tabs.forEach(function (tab, i) {
        var active = i === index;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.setAttribute('tabindex', active ? '0' : '-1');
        var body = tab.querySelector('.ff-app-tab__body');
        if (body) body.hidden = !active;
        if (active && focus) tab.focus();
        if (active && screenLabel) {
          var title = tab.querySelector('.ff-app-tab__title');
          if (title) screenLabel.textContent = title.textContent;
        }
      });
      screens.forEach(function (screen, i) { screen.hidden = i !== index; });
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { select(i, false); }, { signal: signal });
      tab.addEventListener('keydown', function (event) {
        var dir = 0;
        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') dir = 1;
        if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') dir = -1;
        if (!dir) return;
        event.preventDefault();
        select((i + dir + tabs.length) % tabs.length, true);
      }, { signal: signal });
    });

    el.addEventListener('shopify:block:select', function (event) {
      var tab = event.target && event.target.closest('[data-ff-tab]');
      if (tab) select(tabs.indexOf(tab), false);
    }, { signal: signal });

    if (tabs.length) select(0, false);
  }));

  /* -----------------------------------------------------------------------
     <ff-accordion-group> — optional "only one open" behavior for <details>
  ----------------------------------------------------------------------- */
  defineOnce('ff-accordion-group', makeElement(function (el, signal) {
    var single = el.getAttribute('data-single-open') === 'true';
    var items = el.querySelectorAll('details');

    items.forEach(function (item) {
      item.addEventListener('toggle', function () {
        if (!single || !item.open) return;
        items.forEach(function (other) {
          if (other !== item && other.open) other.open = false;
        });
      }, { signal: signal });
    });

    el.addEventListener('shopify:block:select', function (event) {
      var block = event.target && event.target.closest('details');
      if (block) block.open = true;
    }, { signal: signal });
  }));
})();
